import { type FC, useState, useEffect } from 'react';
import { Flex } from '@repo/ui/Flex';
import { Box } from '@repo/ui/Box';
import { Text } from '../Language/Language';
import { ItemCardButtonView } from '../ItemCard/ItemCardView';
import type { ShipmentGroup as ShipmentGroupType } from '../../utils/item';
import s from './ShipmentGroup.module.css';
import sharedStyles from '../shared/ShowMoreLink.module.css';
import type { ItemSelectConfig } from '../../graphql/query/getRetailerSettings';

interface ShipmentGroupProps {
  shipmentGroup: ShipmentGroupType;
  onItemSelect?: (itemId: string, trackingNumber?: string) => void;
  imageSize?: 'sm' | 'md' | 'lg' | 'xl';
  showShipmentDetails?: boolean;
  showMoreCutoff?: number;
  itemSelectConfig?: ItemSelectConfig | null;
}

export const ShipmentGroup: FC<ShipmentGroupProps> = ({
  shipmentGroup,
  onItemSelect,
  imageSize = 'xl',
  showShipmentDetails = true,
  showMoreCutoff = 3,
  itemSelectConfig,
}) => {
  const { items, trackingNumber, shipDate } = shipmentGroup;
  const [showMore, setShowMore] = useState(false);

  const notCutoff = items.length <= showMoreCutoff;

  useEffect(() => {
    setShowMore(notCutoff);
  }, [notCutoff]);

  if (!items || items.length === 0) {
    return null;
  }

  const formatShipDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const ShowMoreButton = () => {
    if (showMore) {
      return null;
    }

    const hiddenItemCount = items.length - showMoreCutoff;

    return (
      <Flex className={s.showMore}>
        <button
          onClick={() => setShowMore(true)}
          className={sharedStyles.showMoreLink}
          type="button"
        >
          <Text
            langKey="Global.showMoreItems"
            replacementTokens={{
              count: hiddenItemCount.toString(),
            }}
          />
        </button>
      </Flex>
    );
  };

  return (
    <Box className={s.shipmentGroup}>
      {showShipmentDetails && (
        <Box className={s.shipmentHeader}>
          <div className={s.shipmentTitle}>
            <Text
              langKey={
                items.length === 1
                  ? 'Global.shipment.shippedDate.singular'
                  : 'Global.shipment.shippedDate.plural'
              }
              replacementTokens={{
                itemCount: items.length.toString(),
                date: shipDate ? formatShipDate(shipDate) : 'Unknown Date',
              }}
            />
          </div>
          {trackingNumber && (
            <div className={s.trackingNumber}>
              <Text
                langKey="Global.shipment.trackingNumber"
                replacementTokens={{
                  trackingNumber: trackingNumber,
                }}
              />
            </div>
          )}
        </Box>
      )}

      <Flex direction="column" gap="g-16" className={s.items}>
        {items.map((item, i) => {
          if (!showMore && i > showMoreCutoff - 1) return null;

          return (
            <ItemCardButtonView
              key={item.id}
              onClick={() => onItemSelect?.(item.id, trackingNumber)}
              aria-labelledby={`${item.id}-title`}
              imageSize={imageSize}
              item={item}
              remainingQuantity={item.quantity?.remaining || 1}
              isShipmentScopeReason={false}
              itemSelectConfig={itemSelectConfig}
            />
          );
        })}
      </Flex>

      <ShowMoreButton />
    </Box>
  );
};
